import React, { useCallback, useRef, useState, useMemo } from "react";
import styled, { css } from "styled-components";
import isEqual from "lodash/isEqual";
import { useTranslation } from "react-i18next";
import { SplitButton } from "components/bootstrap";
import { Col, Row } from "common/components/layout-styles";
import { Icon } from "components/common";
import SearchFilterActionsPopover from "search-filter/components/search-filter-bar/SearchFilterActionsPopover";
import { REFERENCED_USER_SEARCH_FILTER_TYPE } from "search-filter/constants";
import SearchFilterEditPopover from "search-filter/components/search-filter-bar/SearchFilterEditPopover";
import UserFilterShareModal from "search-filter/components/UserFilterShareModal";
import useSearchFiltersFormState from "search-filter/hooks/useSearchFiltersFormState";
import usePermissions from "hooks/usePermissions";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import SearchFilterTitle from "search-filter/components/SearchFilterTitle";
import useScopePermissions from "hooks/useScopePermissions";
import useSearchFiltersFormActions from "../../hooks/useSearchFiltersFormActions";
const FILTER_LIST_WIDTH = 500;
const HelperSpan = styled.span`
  font-size: 70%;
`;
const StyledIcon = styled(Icon)(
  ({ $isDisabled, theme }) => css`
    color: ${$isDisabled ? theme.colors.text.secondary : theme.colors.link.default};
  `
);
const StyledSplitButton = styled(SplitButton)`
  &:not(:only-child):last-child {
    flex-shrink: 0;
  }

  && {
    padding: 0;
    height: auto;
    align-items: flex-start;
  }

  && .mantine-Button-label {
    align-items: flex-start;
  }

  &:hover {
    text-decoration: none;
    background: transparent;
  }

  &:focus {
    text-decoration: none;
  }
`;
const PlaceholderPopoverTarget = styled.div`
  width: 100%;
`;
const Code = styled.code`
  background-color: ${({ theme }) => theme.colors.background.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: balance;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-align: left;
  line-height: 1.4;
  border-radius: 4px;
  padding: 2px 4px;
  text-decoration: ${({ $isDisabled }) => $isDisabled ? "line-through" : "initial"};
`;
const FilterReferenceLabel = ({ filterType, filterId }) => {
  const { isPermitted } = usePermissions();
  const { t } = useTranslation("enterprise_search");
  if (filterType !== REFERENCED_USER_SEARCH_FILTER_TYPE) {
    return null;
  }
  const userIsOwner = isPermitted([`search_filters:delete:${filterId}`]);
  return /* @__PURE__ */ React.createElement(HelperSpan, null, userIsOwner ? t("selected_search_filter.from_my_filters") : t("selected_search_filter.shared_with_me"));
};
const SelectedSearchFilter = ({
  filter,
  filter: { id, queryString, type, frontendId, negation, disabled },
  isParentMutable = true
}) => {
  const { updateFilter } = useSearchFiltersFormActions();
  const filterContainerRef = useRef();
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showEditPopover, setShowEditPopover] = useState(false);
  const [showSharePopover, setShowSharePopover] = useState(false);
  const selectedFilters = useSearchFiltersFormState();
  const sendTelemetry = useSendTelemetry("search-filter");
  const { t } = useTranslation("enterprise_search");
  const { scopePermissions } = useScopePermissions(filter);
  const canChange = useMemo(() => (scopePermissions == null ? void 0 : scopePermissions.is_mutable) && isParentMutable, [scopePermissions, isParentMutable]);
  const toggleShowFilterMenu = useCallback(() => {
    setShowFilterMenu((cur) => !cur);
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_ITEM_MENU_TOGGLED, {
      app_pathname: "search"
    });
  }, [sendTelemetry]);
  const toggleDisableFilter = useCallback(
    (e) => {
      e.preventDefault();
      updateFilter(frontendId, { ...filter, disabled: !disabled });
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_ITEM_DISABLED_TOGGLED, {
        app_pathname: "search",
        app_action_value: "filter-disable",
        event_details: {
          disabled
        }
      });
    },
    [disabled, filter, frontendId, sendTelemetry, updateFilter]
  );
  const toggleShowSharePopover = useCallback(() => {
    setShowSharePopover((cur) => !cur);
    if (!showSharePopover) {
      toggleShowFilterMenu();
    }
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_ITEM_SHARE_CLICKED, {
      app_pathname: "search",
      app_action_value: "filter-share"
    });
  }, [sendTelemetry, showSharePopover, toggleShowFilterMenu]);
  const toggleShowEditPopover = useCallback(() => {
    setShowEditPopover((cur) => !cur);
    if (!showEditPopover) {
      toggleShowFilterMenu();
    }
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_ITEM_EDIT_CLICKED, {
      app_pathname: "search",
      app_action_value: "filter-edit"
    });
  }, [sendTelemetry, showEditPopover, toggleShowFilterMenu]);
  const updateFilterInCurrentSearch = useCallback(
    (newFilter) => {
      const previousFilter = selectedFilters.get(newFilter.frontendId);
      if (!isEqual(newFilter, previousFilter)) {
        updateFilter(newFilter.frontendId, newFilter);
      }
      toggleShowEditPopover();
    },
    [selectedFilters, toggleShowEditPopover, updateFilter]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    StyledSplitButton,
    {
      bsSize: "xsmall",
      bsStyle: "transparent",
      ref: filterContainerRef,
      open: showFilterMenu,
      onMenuChange: setShowFilterMenu,
      id: `filter-menu-popover-split-button-${frontendId}`,
      width: FILTER_LIST_WIDTH,
      title: /* @__PURE__ */ React.createElement(Row, { $align: "center", onClick: canChange ? toggleDisableFilter : void 0 }, canChange ? /* @__PURE__ */ React.createElement(
        StyledIcon,
        {
          name: disabled ? "square" : "check_box",
          $isDisabled: disabled,
          type: disabled ? "regular" : "solid"
        }
      ) : null, /* @__PURE__ */ React.createElement(Row, { $align: "center", "data-testid": "selected-filter-title" }, negation && /* @__PURE__ */ React.createElement("b", null, t("selected_search_filter.negation")), /* @__PURE__ */ React.createElement(Col, { $align: "flex-start", $gap: "xs" }, /* @__PURE__ */ React.createElement(Row, { $align: "baseline", $justify: "space-between" }, /* @__PURE__ */ React.createElement(SearchFilterTitle, { filter, disabled }, filter == null ? void 0 : filter.title), /* @__PURE__ */ React.createElement(FilterReferenceLabel, { filterType: type, filterId: id })), /* @__PURE__ */ React.createElement(Code, { $isDisabled: disabled }, queryString)))),
      dataTestId: `selected-filter-${frontendId}`
    },
    /* @__PURE__ */ React.createElement(
      SearchFilterActionsPopover,
      {
        filter,
        onEditClick: toggleShowEditPopover,
        onShareClick: toggleShowSharePopover,
        onItemClick: toggleShowFilterMenu,
        isParentMutable
      }
    )
  ), showEditPopover && /* @__PURE__ */ React.createElement(SearchFilterEditPopover, { onSave: updateFilterInCurrentSearch, onCancel: toggleShowEditPopover, filter }, /* @__PURE__ */ React.createElement(PlaceholderPopoverTarget, null)), showSharePopover && /* @__PURE__ */ React.createElement(UserFilterShareModal, { filter, toggleShareModalOpen: toggleShowSharePopover }));
};
export default SelectedSearchFilter;
